import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, TextField } from "@mui/material";
import {
  setInputValue,
  setDisplayValue,
  setError,
  setQuantity,
  selectQuantity,
} from "../store/dynamicQteInmputSlice"; // Adjust the path if necessary

const DynamicQuantityInput = () => {
  const dispatch = useDispatch();
  const { selectedArticle } = useSelector((state) => state.article);
  const unitsQte = selectedArticle?.unitsQte || 10;
  const primaryUnit = selectedArticle?.primaryUnits || "unit";
  const secondaryUnit = selectedArticle?.secondaryUnits || "subunit";
  const isAlowSecondaryUnit = selectedArticle?.isAlowSecondaryUnit || false;

  const { inputValue, displayValue, quantityError } = useSelector(selectQuantity);

  useEffect(() => {
    if (!isAlowSecondaryUnit) {
      parseWeightInput(inputValue); // Handle for weight mode
    } else {
      const value = String(inputValue || "").trim(); // Ensure inputValue is a string
      if (/^\d*\.\d+$/.test(value)) {
        // If inputValue is a float (e.g., 2.5), reformat it to primary-secondary
        const [primary, fractional] = value.split(".");
        const secondary = Math.round(parseFloat(`0.${fractional}`) * unitsQte); // Convert fraction to secondary units
        dispatch(setInputValue(`${primary}-${secondary}`)); // Update Redux state with the new format
      } else if (isAlowSecondaryUnit) {
        parsePackBottleInput(value); // Handle for primary-secondary mode
      }
    }
  }, [inputValue, isAlowSecondaryUnit, unitsQte, dispatch]);


useEffect(() => {
  return () => {
    dispatch(setInputValue("")); // Clear the input value when the component is unmounted
  }
},[])
  // Handle the change in input field
  const handleInputChange = (e) => {
    const value = e.target.value.trim();
    dispatch(setInputValue(value)); // Update the Redux state
  };

  // Function to parse input if secondary unit is allowed (for values in primary-secondary format)
  const parsePackBottleInput = (value) => {
    if (!value) {
      dispatch(setDisplayValue(""));
      dispatch(setError(""));
      return;
    }

    value = String(value).trim();

    if (/^\d+$/.test(value)) {
      const packs = parseInt(value, 10);
      dispatch(setDisplayValue(`${packs} ${primaryUnit}${packs !== 1 ? "s" : ""} and 0 ${secondaryUnit}`));
      dispatch(setQuantity(packs)); // Update Redux store with total quantity
      return;
    }

    const parts = value.split("-");

    if (parts.length === 2) {
      const packs = parseInt(parts[0], 10);
      let bottles = parseInt(parts[1], 10);

      if (isNaN(packs) || isNaN(bottles) || bottles < 0) {
        dispatch(setError("Invalid input. Format must be 'primary-secondary units' (e.g., 1-5)."));
        return;
      }

      const extraPacks = Math.floor(bottles / unitsQte);
      bottles %= unitsQte;

      const totalPacks = packs + extraPacks;
      const totalQuantity = totalPacks + bottles / unitsQte;
      dispatch(
        setDisplayValue(
          `${totalPacks} ${primaryUnit}${totalPacks !== 1 ? "s" : ""} and ${bottles} ${secondaryUnit}${bottles !== 1 ? "s" : ""}`
        )
      );
      dispatch(setQuantity(totalQuantity)); // Update Redux store with total quantity
      dispatch(setError("")); // Reset errors if everything is fine
      return;
    }

    dispatch(setError("Invalid format. Use 'primary-secondary unit' (e.g., 1-5) or primary unit only (e.g., 12)."));
  };

  // Function to parse weight input (decimal or integer format)
  const parseWeightInput = (value) => {
    if (!value) {
      dispatch(setDisplayValue(""));
      dispatch(setError(""));
      return;
    }

    if (!/^\d*\.?\d*$/.test(value)) {
      dispatch(setError("Invalid input. Enter a positive or decimal number."));
      return;
    }

    const parsed = parseFloat(value);
    if (isNaN(parsed) || parsed <= 0) {
      dispatch(setError("Invalid input. Enter a positive number."));
      return;
    }

    dispatch(setDisplayValue(`${parsed.toFixed(2)} ${primaryUnit}`));
    dispatch(setQuantity(parsed)); // Update Redux store with the parsed value
    dispatch(setError("")); // Reset error on valid input
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <TextField
        type="text"
        label={isAlowSecondaryUnit ? "Quantity as units-subUnits" : "Quantity"}
        value={inputValue} // Use Redux-managed state for the input value
        onChange={handleInputChange}
        helperText={
          quantityError ||
          (isAlowSecondaryUnit
            ? `Equivalent: ${displayValue}.`
            : `Enter a positive or decimal (e.g., 2.5 ${primaryUnit}).`)
        }
        error={Boolean(quantityError)}
      />
    </Box>
  );
};

export default DynamicQuantityInput;
